import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, TrendingDown, Minus, RefreshCw, BarChart3 } from 'lucide-react';

const TrendIndicator = ({ direction, change }) => {
  if (direction === "up") {
    return (
      <div className="flex items-center gap-1 text-emerald-600">
        <TrendingUp className="w-4 h-4" />
        <span className="text-sm font-medium">+{change?.toFixed(1)}</span>
      </div>
    );
  } else if (direction === "down") {
    return (
      <div className="flex items-center gap-1 text-red-600">
        <TrendingDown className="w-4 h-4" />
        <span className="text-sm font-medium">{change?.toFixed(1)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 text-slate-500">
      <Minus className="w-4 h-4" />
      <span className="text-sm font-medium">Stabil</span>
    </div>
  );
};

const SimpleLineChart = ({ data, dataKey, color = "#f97316", height = 120 }) => {
  if (!data || data.length === 0) return null;

  const values = data.map(d => d[dataKey] || 0);
  const maxVal = Math.max(...values, 100);
  const minVal = Math.min(...values, 0);
  const range = maxVal - minVal || 1;

  // Calculate points for SVG path
  const width = 100;
  const padding = 5;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  
  const points = values.map((val, idx) => {
    const x = padding + (idx / (values.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((val - minVal) / range) * chartHeight;
    return `${x},${y}`;
  });

  const pathD = points.reduce((acc, point, idx) => {
    return idx === 0 ? `M ${point}` : `${acc} L ${point}`;
  }, '');

  // Area fill
  const areaD = `${pathD} L ${padding + chartWidth},${padding + chartHeight} L ${padding},${padding + chartHeight} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }}>
      <defs>
        <linearGradient id={`gradient-${dataKey}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      
      {/* Area fill */}
      <path d={areaD} fill={`url(#gradient-${dataKey})`} />
      
      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      
      {/* Points */}
      {values.map((val, idx) => {
        const x = padding + (idx / (values.length - 1)) * chartWidth;
        const y = padding + chartHeight - ((val - minVal) / range) * chartHeight;
        return (
          <circle key={idx} cx={x} cy={y} r="3" fill="white" stroke={color} strokeWidth="2" />
        );
      })}
    </svg>
  );
};

const BarChart = ({ data, dataKey, color = "#f97316", height = 120 }) => {
  if (!data || data.length === 0) return null;

  const values = data.map(d => d[dataKey] || 0);
  const maxVal = Math.max(...values, 1);

  return (
    <div className="flex items-end justify-between gap-1" style={{ height }}>
      {data.map((d, idx) => {
        const value = d[dataKey] || 0;
        const heightPct = (value / maxVal) * 100;
        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className="w-full rounded-t-sm transition-all duration-300"
              style={{ 
                height: `${heightPct}%`, 
                backgroundColor: color,
                minHeight: 4
              }}
            />
            <span className="text-[10px] text-muted-foreground">
              {d.period?.split('-')[1] || idx + 1}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default function PerformanceTrends({ employeeId }) {
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrends();
  }, [employeeId]);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const endpoint = employeeId 
        ? `/reports/performance-trends/${employeeId}`
        : `/reports/my-trends`;
      const response = await api.get(endpoint);
      setTrendData(response.data);
    } catch (err) {
      console.error('Error fetching trends:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!trendData || trendData.data?.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            Tendințe Performanță
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">Date insuficiente pentru grafic</p>
          <p className="text-sm text-muted-foreground mt-1">
            Graficul va apărea după ce ai date pentru mai multe luni
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="performance-trends">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            Tendințe Performanță
          </div>
          <TrendIndicator 
            direction={trendData.trend_direction} 
            change={trendData.trend_change} 
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Score Trend */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Scor General</span>
            <span className="text-sm text-muted-foreground">
              Ultimele {trendData.months_count} luni
            </span>
          </div>
          <SimpleLineChart data={trendData.data} dataKey="overall_score" color="#f97316" height={100} />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            {trendData.data.map((d, idx) => (
              <span key={idx}>{d.overall_score?.toFixed(0)}</span>
            ))}
          </div>
        </div>

        {/* Utilization Trend */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Utilizare (%)</span>
          </div>
          <BarChart data={trendData.data} dataKey="utilization_pct" color="#10b981" height={80} />
        </div>

        {/* Hours & Tasks */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium">Ore Lucrate</span>
            <BarChart data={trendData.data} dataKey="total_hours" color="#6366f1" height={60} />
          </div>
          <div>
            <span className="text-sm font-medium">Taskuri</span>
            <BarChart data={trendData.data} dataKey="tasks_completed" color="#8b5cf6" height={60} />
          </div>
        </div>

        {/* Period Labels */}
        <div className="flex justify-between text-xs text-muted-foreground border-t pt-2">
          {trendData.data.map((d, idx) => (
            <span key={idx}>{d.period}</span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
